import React, { useState } from 'react';
import {
  Box,
  Button,
  Tabs,
  Tab,
  Paper,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import { useTypedSelector } from 'hooks';
import { Styles } from 'components/Tools';
import {
  ComisionistPrimaryStatusChip,
  ComisionistSecondaryStatusChip,
} from './ComisionistStatusChips';
import {
  getPrimaryStatusLabel,
  getSecondaryStatusLabel,
} from 'utils/statusTranslations';
import { formatDateDDMMYYYY } from 'utils/dateFormatter';
import { DocumentsComisionist } from './DocumentsComisionist';
import { HistoryComisionist } from './HistoryComisionist';

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

type Props = {
  onClose: () => void;
};

export function DetailsComisionist({ onClose }: Props) {
  const classes = Styles();
  const [tabValue, setTabValue] = useState(0);
  const { detail, selectedComisionist, isLoading } = useTypedSelector((s) => s.comisionist);

  if (!selectedComisionist) {
    return (
      <Box p={3}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        bgcolor="#1a237e"
        color="white"
      >
        <Box>
          <Typography variant="caption">
            Cuenta: {selectedComisionist.account}
          </Typography>
          <Typography variant="h6">{selectedComisionist.name}</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <ComisionistPrimaryStatusChip status={selectedComisionist.status} />
          <ComisionistSecondaryStatusChip comisionist={selectedComisionist} />
          <Button color="inherit" onClick={onClose} startIcon={<CloseIcon />} />
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box style={{ maxHeight: 'calc(90vh - 100px)', overflowY: 'auto' }}>
        {/* Alert */}
        {selectedComisionist.documentsStatus === 'PendingVerification' && (
          <Box p={2} bgcolor="#fff3e0" border="1px solid #ffb300" borderRadius={1} m={2}>
            <Typography variant="body2" style={{ color: '#e65100' }}>
              ⚠️ Este comisionista está pendiente de verificación por el área de Finanzas. No se
              puede aprobar hasta completar la revisión.
            </Typography>
          </Box>
        )}

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="comisionist-tabs"
          style={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab label="📋 Datos del Comisionista" />
          <Tab label="📄 Documentos Digitales" />
          <Tab label="🕓 Historial" />
        </Tabs>

        {/* Tab: Datos */}
        <TabPanel value={tabValue} index={0}>
        <Card>
          <CardHeader title="Información General" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Nombre Comisionista
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.name}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Tipo de Comisionista
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#fff8e1' }}>
                  <Typography>{selectedComisionist.type}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Gran Contribuyente
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.isLargeContributor ? 'Sí' : 'No'}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  DUI
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.dui || '—'}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  NIT
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.nit}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  NRC
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.nrc || '—'}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Cuenta AS400
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.account}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Sucursal
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.branchOffice}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Zona
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{selectedComisionist.zone}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Fecha Alta AS400
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{formatDateDDMMYYYY(selectedComisionist.dateHighAS400)}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Estado
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{getPrimaryStatusLabel(selectedComisionist.status)}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="textSecondary">
                  Estado Documental
                </Typography>
                <Paper style={{ padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Typography>{getSecondaryStatusLabel(selectedComisionist.documentsStatus)}</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box mt={3} display="flex" justifyContent="flex-end" style={{ gap: '8px' }}>
              <Button variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="contained" color="primary" disabled={isLoading}>
                Guardar Cambios
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab: Documentos */}
      <TabPanel value={tabValue} index={1}>
        <DocumentsComisionist comisionist={selectedComisionist} />
      </TabPanel>

      {/* Tab: Historial */}
      <TabPanel value={tabValue} index={2}>
        <HistoryComisionist clientAccount={selectedComisionist.account} />
      </TabPanel>
      </Box>
    </Box>
  );
}
